import { i18n } from '@/locales/i18n';
import { ref, computed, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useAppConfigStore } from '@stores/appConfig.store';
import { useUserProfile } from '@stores/userProfile.store';
import { format } from 'date-fns';

import useHttp from '@/utils/httpClient';
const http = useHttp();

const appConfigStore = useAppConfigStore();
const { applicationSetup } = storeToRefs(appConfigStore);

const bookingQueryStore = useBookingQueryStore();
const { query } = storeToRefs(bookingQueryStore);

const userProfileStore = useUserProfile();
const { userProfile } = storeToRefs(userProfileStore);

import { useMobileDevices } from '@composables/useScreenSize';
const { isMobile } = useMobileDevices();

const rooms = ref([]);
const rates = ref([]);
const roomRates = ref([]);
const roomRatePromotions = ref([]);

const offersDataCache = ref({});
const loading = ref(false);
const loadingSameRoom = ref(false);
const currentRoom = ref(0);
const multiRoomList = ref({});
const multiRoomAvailability = ref({});
const highlightAmenities = [
  'sleeps1',
  'sleeps2',
  'sleeps3',
  'bed_size',
  'handicap_accessible',
  'room_size',
  'view',
  'private_terrace',
  'balcony',
  'hair_dryer',
  'bathtub',
];

function getSleepingCapacityKey(maxPersons) {
  switch (maxPersons) {
    case 1:
      return 'sleeps1';
    case 2:
      return 'sleeps2';
    default:
      return 'sleeps3';
  }
}

export const useOffersStore = defineStore('offers', () => {
  const getOffers = async (force = false) => {
    loading.value = true;
    const params = cacheParams.value;
    const cacheKey = cacheKeyFromParams.value;
    let results = offersDataCache.value[cacheKey];

    // Bypass cache if user is logged in
    if (force || !results) {
      results = await http
        .get(params.hotel_id + '/find_offers', params)
        .then((rsp) => {
          return rsp.data;
        });
      processAmenities(results);
      offersDataCache.value[cacheKey] = results;
    }

    processOffers(results);
    loading.value = false;
  };

  const processAmenities = (results) => {
    if (results && Object.keys(results).length > 0) {
      results.rooms.map((room) => {
        let amenities = [
          {
            key: getSleepingCapacityKey(room.max_persons),
            text: i18n.global.t('general.sleeps') + ' ' + room.max_persons,
          },
        ];
        if (room.amenities) {
          let amenitiesP = room.amenities.map((amenity) => {
            let key = amenity
              .toLowerCase()
              .replace(/ /g, '_')
              .replace('/', '')
              .replace('(', '')
              .replace(')', '');
            if (key.includes('bed_size')) {
              key = 'bed_size';
              let splittedA = amenity.split(' ');
              let bedSizeKey = splittedA[splittedA.length - 1].toLowerCase();
              amenity =
                i18n.global.t('amenities.bed_sizes') +
                ' ' +
                i18n.global.t(`amenities.${bedSizeKey}`);
            } else if (key.includes('room_size')) {
              key = 'room_size';
              amenity = amenity.replace('Room size ', '');
            } else {
              amenity = i18n.global.t(`amenities.${key}`);
            }
            return {
              key: key,
              text: amenity,
            };
          });
          amenities = amenities.concat(amenitiesP);
        }

        let highlightedAmenities = amenities.filter((amenity) => {
          return highlightAmenities.includes(amenity.key);
        });
        highlightedAmenities = highlightedAmenities.sort(
          (a, b) =>
            highlightAmenities.indexOf(a.key) -
            highlightAmenities.indexOf(b.key),
        );

        let restAmenities = amenities.filter((amenity) => {
          return !highlightAmenities.includes(amenity.key);
        });

        room.amenities = {
          main: highlightedAmenities,
          secondary: restAmenities,
        };
        return room;
      });
    }
  };

  const processOffers = (results) => {
    if (results && Object.keys(results).length > 0) {
      rooms.value = results.rooms;
      rates.value = [...results.rates, ...results.promotions];
      roomRates.value = results.room_rates;
      roomRatePromotions.value = results.promotions;
    }
    if (isMultiRoom.value) {
      buildMultiRoomList();
    }
  };

  const fillCurrentRoom = (currentRoomIndex, room_rate) => {
    multiRoomList.value[currentRoomIndex].room_rate = room_rate;
    multiRoomList.value[currentRoomIndex].selected = true;
    multiRoomList.value[currentRoomIndex].update = false;
    updateAvailability(room_rate);
  };

  const updateAvailability = (room_rate) => {
    let roomRateSplit = room_rate.split('.');
    let roomCode = roomRateSplit[1];
    let roomRateInfo = getRoomRateInfo(roomRateSplit[0], roomCode);
    if (multiRoomAvailability.value[roomCode] === undefined) {
      multiRoomAvailability.value[roomCode] = Math.min(
        ...roomRateInfo.daily_rates.map((rate) => rate.availability),
      );
    }
    multiRoomAvailability.value[roomCode]--;
  };

  const groupToCurrentRoom = (currentRoomIndex, roomIndex) => {
    fillCurrentRoom(roomIndex, multiRoomList.value[currentRoomIndex].room_rate);
    multiRoomList.value[roomIndex].parent = currentRoomIndex;
  };

  const getRateInfo = (rateId) => {
    return rates.value.find((rate) => rate.rate_plan_code == rateId);
  };

  const getRoomInfo = (roomId) => {
    return rooms.value.find((room) => room.room_type_code == roomId);
  };

  const getRoomRateInfo = (rateId, roomId) => {
    if (!roomRates.value[currentOccupancyKey.value]) {
      return [];
    }
    return roomRates.value[currentOccupancyKey.value].find(
      (roomRate) =>
        roomRate.rate_plan_code == rateId && roomRate.room_type_code == roomId,
    );
  };

  const currentOccupancyKey = computed(() => {
    let currentRoomOccupancy = query.value.occupancy[currentRoom.value];
    if (!currentRoomOccupancy) {
      return;
    }
    return (
      currentRoomOccupancy.nrAdults +
      String(currentRoomOccupancy.nrChildren) +
      String(currentRoomOccupancy.nrBabies)
    );
  });

  const ratesOrder = computed(() => {
    let order = [
      ...new Set(
        roomRates.value[currentOccupancyKey.value].map(
          (item) => item.rate_plan_code,
        ),
      ),
    ];
    return order;
  });

  const roomsOrder = computed(() => {
    let order = [
      ...new Set(
        roomRates.value[currentOccupancyKey.value].map(
          (item) => item.room_type_code,
        ),
      ),
    ];
    return order;
  });

  const resultsByRoom = computed(() => {
    if (!rooms.value || rooms.value.length === 0) {
      return [];
    }
    let sortedData = rooms.value.sort(
      (a, b) =>
        roomsOrder.value.indexOf(a.room_type_code) -
        roomsOrder.value.indexOf(b.room_type_code),
    );
    return sortedData
      .map((room) => {
        const roomRateResults = getRatesInRoom(room.room_type_code);
        let offers = roomRateResults.map((roomRateResult) => {
          const rateInfo = getRateInfo(roomRateResult.rate_plan_code);
          return {
            ...roomRateResult,
            rateInfo,
          };
        });
        return {
          ...room,
          offers,
        };
      })
      .filter((room) => room.offers.length > 0);
  });

  const resultsByRate = computed(() => {
    if (!rates.value || rates.value.length === 0) {
      return [];
    }
    let sortedData = rates.value.sort(
      (a, b) =>
        ratesOrder.value.indexOf(a.rate_plan_code) -
        ratesOrder.value.indexOf(b.rate_plan_code),
    );
    return sortedData
      .map((rate) => {
        const rateRoomResults = getRoomsInRate(rate.rate_plan_code);
        let offers = rateRoomResults.map((rateRoomResult) => {
          const roomInfo = getRoomInfo(rateRoomResult.room_type_code);
          return {
            ...rateRoomResult,
            roomInfo,
          };
        });
        return {
          ...rate,
          offers,
          isLastMinutePromotion: rate.restrictions?.last_minute,
        };
      })
      .filter((rate) => rate.offers.length > 0);
  });

  const getRatesInRoom = (roomId) => {
    if (!roomRates.value[currentOccupancyKey.value]) {
      return [];
    }
    return roomRates.value[currentOccupancyKey.value].filter(
      (roomRate) =>
        roomRate.room_type_code == roomId &&
        (multiRoomAvailability.value[roomRate.room_type_code] === undefined ||
          multiRoomAvailability.value[roomRate.room_type_code] > 0),
    );
  };

  const getRoomsInRate = (rateId) => {
    if (!roomRates.value[currentOccupancyKey.value]) {
      return [];
    }
    return roomRates.value[currentOccupancyKey.value].filter(
      (roomRate) =>
        roomRate.rate_plan_code == rateId &&
        (multiRoomAvailability.value[roomRate.room_type_code] === undefined ||
          multiRoomAvailability.value[roomRate.room_type_code] > 0),
    );
  };

  const setCurrentRoom = (roomIndex) => {
    currentRoom.value = roomIndex;
  };

  const cacheKeyFromParams = computed(() => {
    return (
      'occup_' +
      cacheParams.value.occupancies +
      'cur_' +
      cacheParams.value.currency +
      'start_' +
      cacheParams.value.checkin +
      'end_' +
      cacheParams.value.checkout +
      'lang_' +
      cacheParams.value.language +
      'promo_' +
      cacheParams.value.promotion_code +
      'camp_' +
      cacheParams.value.campaign_code +
      'mob_' +
      cacheParams.value.is_mobile +
      'api_' +
      cacheParams.value.apikey
    );
  });

  const cacheParams = computed(() => {
    const params = {
      hotel_id: applicationSetup.value.hotel_id,
      language: applicationSetup.value.language,
      currency: applicationSetup.value.currency,
      checkin: format(query.value.startDate, 'yyyy-MM-dd'),
      checkout: format(query.value.endDate, 'yyyy-MM-dd'),
      promotion_code: query.value.promotion_code,
      campaign_code: query.value.campaign_code,
      is_mobile: isMobile.value,
      login: userProfile.value.email || userProfile.value.playerId || '',
    };

    if (query.value.filterOffers) {
      params.filter_offers = query.value.filterOffers;
    }

    let occupancies = [];
    for (let i = 0; i < query.value.occupancy.length; i++) {
      occupancies.push({
        adults: query.value.occupancy[i].nrAdults,
        children: query.value.occupancy[i].nrChildren,
        babies: query.value.occupancy[i].nrBabies,
        children_ages: query.value.occupancy[i].children_ages
          ? query.value.occupancy[i].children_ages
          : [],
      });
    }
    params.occupancies = JSON.stringify(occupancies);
    return params;
  });

  const availableRoomsToGroup = computed(() => {
    let availableRooms = {};
    let roomRate = multiRoomList.value[currentRoom.value].room_rate;
    let roomInfo = getRoomInfo(roomRate.split('.')[1]);
    if (
      multiRoomAvailability.value[roomInfo.room_type_code] !== undefined &&
      multiRoomAvailability.value[roomInfo.room_type_code] === 0
    ) {
      return availableRooms;
    }
    let remainingAvailability = null;
    if (multiRoomAvailability.value[roomInfo.room_type_code]) {
      remainingAvailability =
        multiRoomAvailability.value[roomInfo.room_type_code];
    }

    for (let i in multiRoomList.value) {
      if (remainingAvailability === 0) {
        continue;
      }
      if (multiRoomList.value[i].selected) {
        continue;
      }
      if (roomInfo.max_adults < multiRoomList.value[i].occupancy.nrAdults) {
        continue;
      }
      let totalChildren =
        multiRoomList.value[i].occupancy.nrChildren +
        multiRoomList.value[i].occupancy.nrBabies;
      if (roomInfo.max_children < totalChildren) {
        continue;
      }
      if (
        roomInfo.max_persons <
        multiRoomList.value[i].occupancy.nrAdults + totalChildren
      ) {
        continue;
      }
      availableRooms[i] = {
        label:
          i18n.global.t('general.room') +
          ' ' +
          (multiRoomList.value[i].identifier + 1),
        value: multiRoomList.value[i].identifier,
      };
      if (remainingAvailability !== null) {
        remainingAvailability--;
      }
    }
    return availableRooms;
  });

  const isMultiRoom = computed(() => {
    return query.value.occupancy.length > 1;
  });

  const missingMultipleSelection = computed(() => {
    return Object.values(multiRoomList.value).some((room) => !room.selected);
  });

  const buildMultiRoomList = () => {
    for (let i in query.value.occupancy) {
      if (multiRoomList.value[i] && multiRoomList.value[i].selected == true) {
        continue;
      }
      let multiRoom = {
        key:
          query.value.occupancy[i].nrAdults +
          String(query.value.occupancy[i].nrChildren) +
          String(query.value.occupancy[i].nrBabies),
        occupancy: query.value.occupancy[i],
        identifier: parseInt(i),
        room_rate: '',
        selected: false,
      };
      multiRoomList.value[i] = multiRoom;
    }
  };

  const restoreAvailability = (roomCode) => {
    if (multiRoomAvailability.value[roomCode] !== undefined) {
      multiRoomAvailability.value[roomCode]++;
    }
  };

  const checkCurrentRoomSelected = () => {
    if (!isMultiRoom.value) {
      return;
    }

    if (multiRoomList.value[currentRoom.value].update) {
      return;
    }

    if (missingMultipleSelection.value) {
      let notSelected = Object.values(multiRoomList.value).some(
        (room) => room.selected === false,
      );
      if (notSelected) {
        for (let room of Object.values(multiRoomList.value)) {
          if (!room.selected) {
            currentRoom.value = room.identifier;
            return;
          }
        }
      }
    }

    if (
      currentRoom.value === Object.keys(multiRoomList.value).length - 1 &&
      multiRoomList.value[currentRoom.value].parent !== undefined
    ) {
      currentRoom.value = multiRoomList.value[currentRoom.value].parent;
      return;
    }

    if (
      !missingMultipleSelection.value ||
      currentRoom.value > Object.keys(multiRoomList.value).length - 1
    ) {
      const lastAvailableRoom =
        multiRoomList.value[Object.keys(multiRoomList.value).length - 1];
      if (lastAvailableRoom) {
        if (lastAvailableRoom.parent !== undefined) {
          currentRoom.value = lastAvailableRoom.parent;
          return;
        }
        currentRoom.value = lastAvailableRoom.identifier;
        return;
      }
    }
  };

  watch(
    cacheParams,
    () => {
      if (!cacheParams.value.currency) {
        return;
      }
      getOffers();
    },
    { immediate: true },
  );

  return {
    loading,
    currentRoom,
    isMultiRoom,
    multiRoomList,
    availableRoomsToGroup,
    resultsByRoom,
    resultsByRate,
    missingMultipleSelection,
    multiRoomAvailability,
    roomRates,
    roomRatePromotions,
    loadingSameRoom,
    getOffers,
    getRateInfo,
    getRoomInfo,
    getRoomRateInfo,
    setCurrentRoom,
    groupToCurrentRoom,
    fillCurrentRoom,
    buildMultiRoomList,
    checkCurrentRoomSelected,
    restoreAvailability,
  };
});
